import { getIdv2StoreData } from '@onefootprint/idv2';
import type { OnboardingRequirement } from '@onefootprint/request-types';
import type { TransitionConfig, TransitionsConfig } from 'xstate';
import { ComponentsSdkTypes } from '@/idv/utils/state-machine/types';
import type { MachineContext, MachineEvents } from './types';

export const getPreferUploadDoc = <K extends OnboardingRequirement>(docRequirement: K[]) =>
  docRequirement.find(r => r.kind === 'collect_document' && r.uploadSettings === 'prefer_upload');

const getFirstKind = (c: MachineContext): OnboardingRequirement['kind'] => c.requirements[0]?.kind;

const isDeviceMobile = (c: MachineContext): boolean => c?.idvContext?.device?.type === 'mobile';
const isDeviceTablet = (c: MachineContext): boolean => c?.idvContext?.device?.type === 'tablet';
const isTransfer = (c: MachineContext): boolean => Boolean(c?.idvContext?.isTransfer);

const isCollectDoc = (x: unknown) => x === 'collect_document';
const isRegisterPasskey = (x: unknown) => x === 'liveness';
const isProcess = (x: unknown) => x === 'process';
const isSleep = (x: unknown) => x === 'sleep';

const shouldRunLiveness = (ctx: MachineContext) => isRegisterPasskey(getFirstKind(ctx));
const shouldShowProcess = (ctx: MachineContext) => !isTransfer(ctx) && isProcess(getFirstKind(ctx));
const shouldRunSleep = (ctx: MachineContext) => isSleep(getFirstKind(ctx));

const shouldRunIdDoc = (ctx: MachineContext): boolean => {
  const isTabletOrMobile = isDeviceMobile(ctx) || isDeviceTablet(ctx);
  if (isTransfer(ctx) && !isTabletOrMobile) {
    // If we're running the transfer app on desktop, we want to keep the transfer as small as
    // possible. So, only register the passkey, don't allow also uploading id doc in desktop transfer
    return false;
  }
  return isCollectDoc(getFirstKind(ctx));
};

export const requirementCompletedTransition = (
  actions: string[],
): TransitionsConfig<MachineContext, MachineEvents> => ({
  requirementCompleted: { target: 'checkRequirements', actions },
});

/**
 * ORDER MATTERS.
 * The ordering of these targets actually dictates the order in which requirements are handled by the frontend
 */
export const NextRequirementTargets: TransitionConfig<MachineContext, MachineEvents>[] = [
  { target: 'liveness', cond: context => shouldRunLiveness(context) },
  { target: 'idDoc', cond: context => shouldRunIdDoc(context) },
  { target: 'sleep', cond: context => shouldRunSleep(context) },
  { target: 'process', cond: context => shouldShowProcess(context) },
  { target: 'success' },
];

export const shouldWaitForComponentsSdk = (context: MachineContext) => {
  if (getIdv2StoreData()?.componentsSdkRelayCompleted) {
    return false;
  }

  const componentSdkContext = context.idvContext.componentsSdkContext;
  if (!componentSdkContext) {
    return false;
  }

  const { componentsSdkType, skipRelayToComponents } = componentSdkContext;
  if (skipRelayToComponents) {
    return false;
  }
  if (componentsSdkType === ComponentsSdkTypes.WEB) {
    return !!context.idvContext.isInIframe;
  }
  return componentsSdkType === ComponentsSdkTypes.MOBILE;
};
